import { useEffect, useState } from 'react';
import { i18n, wordCount, staffFeatureAllowed } from '@wms/shared';
import { canWriteStaffScope } from '../lib/staff-access.js';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { ProductConfigurator } from './ProductConfigurator.js';
import { SearchSelect } from './SearchSelect.js';

const { t } = i18n;

interface ProductOpt {
  id: string;
  code: string;
  type: 'PRODUCT' | 'COMPONENT';
  nameLt: string;
  photoUrl: string | null;
  availableQty: number;
  maxProducibleQty: number | null;
}

interface DraftLine {
  productSkuId: string;
  qty: number;
  comment: string;
  sortOrder: number;
}

export function OrderLinesEditor({
  orderId,
  partnerId,
  existing,
  onClose,
  onSaved,
  amend = false,
}: {
  orderId: string;
  partnerId: string;
  existing: DraftLine[];
  onClose: () => void;
  onSaved: () => void;
  /** Amend an accepted order — requires a ≥2-word justification comment and
   *  posts to /amend instead of replacing draft lines (QA #8 / WMS-9). */
  amend?: boolean;
}) {
  const { user } = useAuth();
  const [products, setProducts] = useState<ProductOpt[]>([]);
  const [lines, setLines] = useState<DraftLine[]>(existing);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [configuratorOpen, setConfiguratorOpen] = useState(false);
  // Mirrors `sku.write` in rbac.matrix.ts — an ORDER_PLACER may search the
  // catalogue by components but cannot add products to it (WMS-46).
  const canWriteSkus =
    staffFeatureAllowed(user, 'skuCreate') &&
    canWriteStaffScope(user, 'PARTNER', partnerId) &&
    (user?.roles ?? []).some((r) => r.role === 'ADMIN' || r.role === 'PARTNER_ADMIN');

  // `productsKey` forces a refetch after the configurator creates a product, so
  // the freshly-picked line resolves to a real label instead of a blank one.
  const [productsKey, setProductsKey] = useState(0);
  useEffect(() => {
    api<ProductOpt[]>(`/partners/${partnerId}/skus`)
      .then(setProducts)
      .catch(() => setProducts([]));
  }, [partnerId, productsKey]);

  function addLine() {
    if (products.length === 0) return;
    if (lines.some((line) => !line.productSkuId)) return;
    setLines((prev) => [
      ...prev,
      { productSkuId: '', qty: 1, comment: '', sortOrder: prev.length },
    ]);
  }

  const linesValid = lines.every((line) => Boolean(line.productSkuId) && line.qty > 0);

  async function save() {
    if (amend && wordCount(comment) < 2) {
      setError(t('order.amend.comment_too_short'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = { lines: lines.map((l, i) => ({ ...l, sortOrder: i })) };
      if (amend) {
        await api(`/orders/${orderId}/amend`, {
          method: 'POST',
          body: { ...body, comment },
        });
      } else {
        await api(`/orders/${orderId}/lines`, { method: 'PUT', body });
      }
      onSaved();
    } catch {
      setError(t('common.error'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Dialog
        title={amend ? t('order.amend.title') : t('order.lines.edit')}
        onClose={onClose}
        size="wide"
        height="tall"
      >
        <div className="space-y-3 text-sm">
          {products.length === 0 ? (
            <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
              {t('order.lines.no_skus')}
            </div>
          ) : null}
          {lines.map((line, i) => (
            <div key={i} className="flex items-center gap-2 rounded border border-border p-2">
              <SearchSelect
                className="min-w-0 flex-1"
                menuSize="tall"
                ariaLabel={t('order.lines.pick_sku')}
                value={line.productSkuId}
                onChange={(id) =>
                  setLines((d) => d.map((l, j) => (j === i ? { ...l, productSkuId: id } : l)))
                }
                options={products.map((p) => ({
                  id: p.id,
                  label: `[${t(`sku.type.${p.type}`)}] ${p.code} — ${p.nameLt}`,
                  imageUrl: p.photoUrl,
                  availableQty: p.availableQty,
                  maxProducibleQty: p.maxProducibleQty ?? undefined,
                }))}
              />
              <NumberInput
                integer
                min={1}
                value={line.qty}
                onChange={(v) =>
                  setLines((d) => d.map((l, j) => (j === i ? { ...l, qty: v ?? 0 } : l)))
                }
                className="w-24 shrink-0 rounded border border-border bg-background px-2 py-1 tabular-nums"
              />
              <button
                type="button"
                onClick={() => setLines((d) => d.filter((_, j) => j !== i))}
                className="shrink-0 rounded border border-border px-2 py-1 text-xs"
              >
                ×
              </button>
            </div>
          ))}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={addLine}
              disabled={products.length === 0 || lines.some((line) => !line.productSkuId)}
              className="rounded border border-border px-3 py-1.5 text-sm disabled:opacity-50"
            >
              {t('order.lines.add')}
            </button>
            {/* WMS-46: when you know what the thing is made of but not what it is
              called, find it by its components instead of by name. */}
            <button
              type="button"
              onClick={() => setConfiguratorOpen(true)}
              className="rounded border border-border px-3 py-1.5 text-sm"
            >
              {t('configurator.action.order')}
            </button>
          </div>

          {amend ? (
            <label className="block space-y-1">
              <span className="font-medium">{t('order.amend.comment')}</span>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={2}
                placeholder={t('order.amend.comment_hint')}
                className="w-full rounded border border-border bg-background px-2 py-1"
              />
            </label>
          ) : null}

          {error ? <div className="text-red-600">{error}</div> : null}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded border border-border px-3 py-1.5"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !linesValid}
              className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
            >
              {busy ? t('common.loading') : t('common.save')}
            </button>
          </div>
        </div>
      </Dialog>
      {/* A DOM sibling of the editor dialog, not a child: nesting it would put
          its focusable elements inside the outer focus trap and the two traps
          would fight over Tab at the boundaries. */}
      {configuratorOpen ? (
        <ProductConfigurator
          partnerId={partnerId}
          canCreate={canWriteSkus}
          onClose={() => setConfiguratorOpen(false)}
          onPick={(product) => {
            setLines((prev) => [
              ...prev,
              { productSkuId: product.id, qty: 1, comment: '', sortOrder: prev.length },
            ]);
            setProductsKey((k) => k + 1);
            setConfiguratorOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
